import React, { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, CreditCard, Check } from 'lucide-react';
import UserInfo from '@/features/account/components/UserInfo';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { playClickSound } from '@/utils/soundUtils';
import { requiresEvenAmount, validateAmountForGame, isEvenAmount } from '@/utils/gameValidationUtils';

interface SelectAmountToDepositProps {
    selectedProvider: {
        id: number | string;
        name: string;
        tagline: string;
        icon: string; // Path to icon image
        minDeposit?: number; // from admin config (GET /game-providers already returns it); fallback 5
    };
    authenticatedUser: {
        email: string;
    };
    onBack: () => void;
    onAmountSelected: (amount: number) => void;
}

interface AmountOption {
    value: number;
    label: string;
    popular?: boolean;
}

export default function SelectAmountToDeposit({
    selectedProvider,
    authenticatedUser,
    onBack,
    onAmountSelected
}: SelectAmountToDepositProps) {
    // Min deposit from admin config (per-provider); fallback 5 = DB default. Max stays fixed at 1000.
    const minDep = selectedProvider.minDeposit ?? 5;

    // Full-screen background
    const fullScreenBg = usePageFullScreenBackground();

    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [customAmount, setCustomAmount] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [isCustomAmountValid, setIsCustomAmountValid] = useState<boolean>(false);

    // Predefined amount options
    const amountOptions: AmountOption[] = [
        { value: 5, label: '$5' },
        { value: 20, label: '$20' },
        { value: 50, label: '$50' },
        { value: 100, label: '$100', popular: true },
        { value: 200, label: '$200' },
        { value: 500, label: '$500' },
        { value: 1000, label: '$1000' }
    ];

    // Get card styling classes for amount cards with enhanced highlighting
    const getCardClasses = (option: AmountOption, isSelected: boolean) => {
        if (isSelected) {
            if (option.popular) {
                return 'bg-gradient-to-br from-amber-500/90 to-orange-600/90 border-2 border-amber-300 text-white shadow-xl shadow-amber-500/40 scale-105 ring-2 ring-amber-300/50';
            } else if (option.value <= 50) {
                return 'bg-gradient-to-br from-green-500/90 to-emerald-600/90 border-2 border-green-300 text-white shadow-xl shadow-green-500/40 scale-105 ring-2 ring-green-300/50';
            } else if (option.value <= 200) {
                return 'bg-gradient-to-br from-blue-500/90 to-cyan-600/90 border-2 border-blue-300 text-white shadow-xl shadow-blue-500/40 scale-105 ring-2 ring-blue-300/50';
            } else {
                return 'bg-gradient-to-br from-purple-500/90 to-pink-600/90 border-2 border-purple-300 text-white shadow-xl shadow-purple-500/40 scale-105 ring-2 ring-purple-300/50';
            }
        }

        // Default (unselected) styles with enhanced colors
        if (option.popular) {
            return 'bg-gradient-to-br from-amber-600/70 to-orange-700/70 border-2 border-amber-400/60 text-white hover:from-amber-500/80 hover:to-orange-600/80 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-500/30 hover:scale-105 transition-all duration-200';
        } else if (option.value <= 50) {
            return 'bg-gradient-to-br from-green-600/70 to-emerald-700/70 border-2 border-green-400/60 text-white hover:from-green-500/80 hover:to-emerald-600/80 hover:border-green-300 hover:shadow-lg hover:shadow-green-500/30 hover:scale-105 transition-all duration-200';
        } else if (option.value <= 200) {
            return 'bg-gradient-to-br from-blue-600/70 to-cyan-700/70 border-2 border-blue-400/60 text-white hover:from-blue-500/80 hover:to-cyan-600/80 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 transition-all duration-200';
        } else {
            return 'bg-gradient-to-br from-purple-600/70 to-pink-700/70 border-2 border-purple-400/60 text-white hover:from-purple-500/80 hover:to-pink-600/80 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/30 hover:scale-105 transition-all duration-200';
        }
    };

    // Validate custom amount
    useEffect(() => {
        if (customAmount.trim() === '') {
            setValidationError('');
            setIsCustomAmountValid(false);
            return;
        }

        const amount = parseFloat(customAmount);

        if (isNaN(amount)) {
            setValidationError('Please enter a valid number');
            setIsCustomAmountValid(false);
        } else if (amount < minDep) {
            setValidationError(`Minimum deposit amount is $${minDep}`);
            setIsCustomAmountValid(false);
        } else if (amount > 1000) {
            setValidationError('The maximum deposit amount is $1,000. Please enter a smaller amount.');
            setIsCustomAmountValid(false);
        } else {
            // Validate even amount for specific games (pandamaster, firekirin)
            const amountValidation = validateAmountForGame(amount, selectedProvider);
            if (!amountValidation.isValid) {
                setValidationError(amountValidation.message);
                setIsCustomAmountValid(false);
            } else {
                setValidationError('');
                setIsCustomAmountValid(true);
            }
        }
    }, [customAmount, selectedProvider]);

    const handleBackClick = () => {
        playClickSound();
        onBack();
    };

    const handleAmountSelect = (amount: number) => {
        // Validate even amount for specific games (pandamaster, firekirin)
        const amountValidation = validateAmountForGame(amount, selectedProvider);
        if (!amountValidation.isValid) {
            setValidationError(amountValidation.message);
            return;
        }

        playClickSound();
        setSelectedAmount(amount);
        setCustomAmount(''); // Clear custom amount when selecting predefined
        setValidationError('');
    };

    const handleCustomAmountChange = (value: string) => {
        // Only allow numbers and one decimal point
        let sanitized = value.replace(/[^0-9.]/g, '');

        // If game requires whole number (no decimals), block decimal input
        if (requiresEvenAmount(selectedProvider)) {
            // Block decimal point and decimal digits
            if (sanitized.includes('.')) {
                return;
            }
        }

        // Prevent multiple decimal points
        const parts = sanitized.split('.');
        if (parts.length > 2) {
            return;
        }

        // Limit to 2 decimal places
        if (parts[1] && parts[1].length > 2) {
            return;
        }

        setCustomAmount(sanitized);
        setSelectedAmount(null); // Clear predefined selection when typing custom
    };

    const handleCustomAmountBlur = () => {
        if (customAmount.trim() !== '' && isCustomAmountValid) {
            const amount = parseFloat(customAmount);
            // Round to 2 decimal places
            const roundedAmount = Math.round(amount * 100) / 100;
            setCustomAmount(roundedAmount.toString());
        }
    };

    const handleContinue = () => {
        playClickSound();
        let finalAmount: number;

        if (selectedAmount !== null) {
            finalAmount = selectedAmount;
        } else if (isCustomAmountValid) {
            finalAmount = parseFloat(customAmount);
        } else {
            setValidationError('Please select an amount or enter a valid custom amount');
            return;
        }

        // Redirect to deposit page with amount parameter
        const depositUrl = `/account/finance/deposit?amount=${finalAmount}&provider=${selectedProvider.name.toLowerCase().replace(/\s+/g, '-')}`;
        window.location.href = depositUrl;
    };

    const getCurrentAmount = (): number | null => {
        if (selectedAmount !== null) {
            return selectedAmount;
        }
        if (isCustomAmountValid) {
            return parseFloat(customAmount);
        }
        return null;
    };

    const currentAmount = getCurrentAmount();

    return (
        <div {...fullScreenBg} className={`${fullScreenBg.className} flex items-center justify-center p-4 py-8`}>
            <div className="w-full max-w-2xl">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={handleBackClick}
                        className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-sm">Back to Actions</span>
                    </button>

                    <div className="text-center">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                            Select Deposit Amount
                        </h1>

                        <div className="flex items-center justify-center gap-3 text-white/80">
                            <img
                                src={selectedProvider.icon}
                                alt={selectedProvider.name}
                                className="w-5 h-5 object-contain"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/icons/egame.jpg';
                                }}
                            />
                            <span className="text-sm sm:text-base">{selectedProvider.name}</span>
                            <span className="text-white/40">•</span>
                            <span className="text-sm sm:text-base font-medium text-green-400">Deposit</span>
                        </div>

                        {/* User Email Display */}
                        <div className="mt-3">
                            <UserInfo email={authenticatedUser.email} />
                        </div>
                    </div>
                </div>

                {/* Selected Amount Display */}
                {currentAmount && (
                    <div className="inline-flex items-center gap-2 bg-green-500/10 backdrop-blur-md rounded-full px-4 py-2 mb-4 border border-green-400/30">
                        <DollarSign className="w-4 h-4 text-green-300" />
                        <span className="text-lg font-bold text-green-100">
                            ${currentAmount.toFixed(2)}
                        </span>
                        <Check className="w-4 h-4 text-green-300" />
                        <span className="text-green-200 text-sm font-medium">
                            Ready to deposit
                        </span>
                    </div>
                )}

                {/* Predefined Amount Options */}
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-white mb-4 text-center">
                        Choose Amount
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {(() => {
                            // All preset amounts are whole numbers, so no filtering needed
                            return amountOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleAmountSelect(option.value)}
                                    className={`
                                        relative p-4 rounded-lg border-2 transition-all duration-200 text-center transform
                                        ${getCardClasses(option, selectedAmount === option.value)}
                                    `}
                                >
                                    <div className="font-black text-xl drop-shadow-lg relative">
                                        {option.label}
                                        {option.value <= 50 && (
                                            <div className="absolute -inset-1 bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-lg blur-sm -z-10"></div>
                                        )}
                                    </div>
                                    {option.value <= 50 && (
                                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-xl border-2 border-white/20">
                                            Basic
                                        </div>
                                    )}
                                    {option.popular && (
                                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-xl border-2 border-white/20">
                                            ⭐ Popular
                                        </div>
                                    )}
                                    {selectedAmount === option.value && (
                                        <div className="absolute top-1 right-1 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full p-1.5 shadow-xl border-2 border-white/30">
                                            <Check className="w-4 h-4 text-white font-bold" />
                                        </div>
                                    )}
                                </button>
                            ));
                        })()}
                    </div>
                </div>

                {/* Custom Amount Input - Secondary Option */}
                <div className="mb-6">
                    <div className="text-center mb-4">
                        <span className="text-sm text-white/60">or</span>
                    </div>

                    <div className="relative max-w-sm mx-auto">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <DollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={customAmount}
                            onChange={(e) => handleCustomAmountChange(e.target.value)}
                            onBlur={handleCustomAmountBlur}
                            placeholder="Enter custom amount"
                            maxLength={30}
                            className={`
                                block w-full pl-10 pr-3 py-3 border rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-center text-base font-mono
                                ${validationError
                                    ? 'border-red-400/60 focus:ring-red-500/60'
                                    : isCustomAmountValid
                                        ? 'border-green-400/60 focus:ring-green-500/60'
                                        : 'border-gray-400/40 focus:ring-purple-500/60'
                                }
                            `}
                        />
                        {isCustomAmountValid && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                <Check className="h-5 w-5 text-green-400" />
                            </div>
                        )}
                    </div>

                    {validationError && (
                        <p className="text-red-400 text-sm text-center mt-2">
                            {validationError}
                        </p>
                    )}

                    <p className="text-white/50 text-sm text-center mt-2">
                        Minimum: ${minDep.toFixed(2)} • Maximum: $1,000.00
                    </p>
                </div>

                {/* Continue Button - Primary Action */}
                <div className="text-center mb-8">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-white mb-1">Ready to Deposit?</h3>
                        <p className="text-sm text-white/70">Complete your transaction securely</p>
                    </div>

                    <button
                        onClick={handleContinue}
                        disabled={!currentAmount}
                        className={`
                            inline-flex items-center gap-3 px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-200 transform shadow-lg
                            ${currentAmount
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 active:from-green-800 active:to-emerald-800 hover:shadow-green-500/40 hover:scale-105 active:scale-95 border-2 border-white/20'
                                : 'bg-gray-600 text-gray-400 cursor-not-allowed border-2 border-gray-500/30'
                            }
                        `}
                    >
                        <CreditCard className="w-5 h-5" />
                        Continue to Payment
                        {currentAmount && (
                            <div className="ml-2 px-2 py-1 bg-white/20 rounded-full text-sm font-bold">
                                ${currentAmount.toFixed(2)}
                            </div>
                        )}
                    </button>

                    {currentAmount && (
                        <p className="text-green-300 text-sm mt-3 font-medium">
                            ✓ Amount selected: ${currentAmount.toFixed(2)}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
